import { useEffect, useState, useCallback } from 'react';
import api from './axiosSetup';
import CustomNavbar from './CustomNavbar';
import cfg from '../config';

const formatDt = (dt) =>
  dt ? new Date(dt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '–';

const isExpired = (deadline) => new Date(deadline) < new Date();

// ── Stil-Konstanten ──────────────────────────────────────────
const S = {
  card:       { background: '#1e2130', border: '1px solid #2e3347', borderRadius: 10 },
  cardHeader: { background: '#252839', borderBottom: '1px solid #2e3347', padding: '0.9rem 1.2rem', borderRadius: '10px 10px 0 0', cursor: 'pointer' },
  muted:      { color: '#7b82a0', fontSize: '0.82rem' },
  badge: {
    expired:   { background: '#3a3f55', color: '#9aa3bc', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20 },
    voted:     { background: '#1a4a2a', color: '#4ade80', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20 },
    zunftrat:  { background: '#1a4a7a', color: '#7ec8ff', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20 },
  },
};

const PollCard = ({ poll, onVoted }) => {
  const [detail, setDetail]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState([]);
  const [voting, setVoting]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const [error, setError]       = useState(null);

  const loadDetail = useCallback(async () => {
    if (detail) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/polls/${poll.id}`);
      setDetail(res.data);
      setSelected(res.data.myVotes || []);
    } catch { setError('Fehler beim Laden.'); }
    finally { setLoading(false); }
  }, [poll.id, detail]);

  const toggle = async () => {
    if (!expanded) await loadDetail();
    setExpanded(e => !e);
  };

  const toggleOption = (id) => {
    if (detail?.isMultiChoice) {
      setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    } else {
      setSelected([id]);
    }
  };

  const handleVote = async () => {
    setError(null); setMsg(null);
    if (!selected.length) { setError('Bitte wähle mindestens eine Option.'); return; }
    setVoting(true);
    try {
      await api.post(`/api/polls/${poll.id}/vote`, { optionIds: selected });
      setMsg('Deine Stimme wurde erfolgreich abgegeben!');
      setDetail((await api.get(`/api/polls/${poll.id}`)).data);
      onVoted?.();
    } catch (err) { setError(err.response?.data?.message || 'Fehler beim Abstimmen.'); }
    finally { setVoting(false); }
  };

  const expired   = isExpired(poll.deadline);
  const hasVoted  = poll.hasVoted > 0;
  const showResults = hasVoted || expired;
  const totalVotes  = detail?.options?.reduce((s, o) => s + (o.voteCount || 0), 0) ?? poll.totalVotes;

  // Restzeit berechnen
  const msLeft = new Date(poll.deadline) - new Date();
  const hoursLeft = Math.floor(msLeft / 3_600_000);
  const daysLeft  = Math.floor(msLeft / 86_400_000);
  const timeLabel = expired ? null
    : daysLeft >= 2 ? `Noch ${daysLeft} Tage`
    : hoursLeft >= 1 ? `Noch ${hoursLeft} Std.`
    : 'Endet bald';

  return (
    <div style={{ ...S.card, marginBottom: 12 }}>
      {/* ── Header (immer sichtbar, klickbar) ── */}
      <div style={S.cardHeader} onClick={toggle}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span style={{ color: '#e8eaf0', fontWeight: 600, fontSize: '0.97rem' }}>{poll.title}</span>
            {expired    && <span style={S.badge.expired}>Abgelaufen</span>}
            {hasVoted   && <span style={S.badge.voted}>✓ Abgestimmt</span>}
            {!!poll.forZunftrat && <span style={S.badge.zunftrat}>{cfg.adminRoleName}</span>}
          </div>
          <div className="d-flex align-items-center gap-3">
            {timeLabel && (
              <span style={{ color: daysLeft < 1 ? '#f87171' : '#facc15', fontSize: '0.78rem', fontWeight: 600 }}>
                {timeLabel}
              </span>
            )}
            <span style={{ color: '#4a5068', fontSize: 18, userSelect: 'none' }}>{expanded ? '▲' : '▼'}</span>
          </div>
        </div>
        <div style={{ marginTop: 4 }}>
          <span style={S.muted}>Frist: {formatDt(poll.deadline)}</span>
          {poll.totalVotes > 0 && <span style={{ ...S.muted, marginLeft: 12 }}>{poll.totalVotes} Stimme{poll.totalVotes !== 1 ? 'n' : ''}</span>}
        </div>
      </div>

      {/* ── Body (aufklappbar) ── */}
      {expanded && (
        <div style={{ padding: '1.1rem 1.2rem' }}>
          {loading && <div className="text-center py-3"><div className="spinner-border spinner-border-sm" style={{ color: '#ffc107' }} /></div>}
          {error   && <div className="alert alert-danger py-2 small">{error}</div>}
          {msg     && <div style={{ background: '#1a3a2a', border: '1px solid #2a5a3a', color: '#4ade80', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '0.9rem' }}>{msg}</div>}

          {detail && (
            <>
              {detail.description && (
                <p style={{ color: '#9aa3bc', marginBottom: 16, fontSize: '0.9rem' }}>{detail.description}</p>
              )}

              {showResults ? (
                /* ── Ergebnisbalken ── */
                <div>
                  {detail.options.map(opt => {
                    const pct = totalVotes > 0 ? Math.round((opt.voteCount / totalVotes) * 100) : 0;
                    const isMyVote = (detail.myVotes || []).includes(opt.id);
                    return (
                      <div key={opt.id} style={{ marginBottom: 14 }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span style={{ color: isMyVote ? '#ffc107' : '#c8cfe0', fontWeight: isMyVote ? 700 : 400, fontSize: '0.9rem' }}>
                            {isMyVote ? '✓ ' : ''}{opt.label}
                          </span>
                          <span style={{ color: '#7b82a0', fontSize: '0.82rem', flexShrink: 0, marginLeft: 8 }}>
                            {opt.voteCount} ({pct}%)
                          </span>
                        </div>
                        <div style={{ background: '#13151f', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 6, transition: 'width 0.4s ease',
                            width: `${pct}%`,
                            background: isMyVote ? '#ffc107' : '#3a4a6a',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                  <span style={S.muted}>Gesamt: {totalVotes} Stimme{totalVotes !== 1 ? 'n' : ''}</span>
                </div>
              ) : (
                /* ── Abstimmungs-UI ── */
                <div>
                  {!!detail.isMultiChoice && (
                    <p style={{ ...S.muted, marginBottom: 10 }}>Mehrfachauswahl möglich – wähle alle zutreffenden Optionen.</p>
                  )}
                  <div style={{ marginBottom: 16 }}>
                    {detail.options.map(opt => {
                      const isSelected = selected.includes(opt.id);
                      return (
                        <div
                          key={opt.id}
                          onClick={() => toggleOption(opt.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 14px', marginBottom: 8, borderRadius: 8, cursor: 'pointer',
                            background: isSelected ? '#1a2a3a' : '#13151f',
                            border: `1px solid ${isSelected ? '#ffc107' : '#2e3347'}`,
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <div style={{
                            width: 18, height: 18, flexShrink: 0,
                            borderRadius: detail.isMultiChoice ? 4 : '50%',
                            border: `2px solid ${isSelected ? '#ffc107' : '#4a5068'}`,
                            background: isSelected ? '#ffc107' : 'transparent',
                            transition: 'all 0.15s ease',
                          }} />
                          <span style={{ color: isSelected ? '#ffc107' : '#c8cfe0', fontSize: '0.93rem' }}>
                            {opt.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={handleVote}
                    disabled={voting || !selected.length}
                    className="btn btn-warning fw-semibold"
                    style={{ borderRadius: 8, padding: '8px 24px' }}
                  >
                    {voting ? 'Wird gespeichert…' : 'Jetzt abstimmen'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Haupt-Seite ──────────────────────────────────────────────
const Polls = () => {
  const [polls, setPolls]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState('offen');

  const load = useCallback(async () => {
    setLoading(true);
    try { setPolls((await api.get('/api/polls')).data || []); }
    catch { setError('Fehler beim Laden.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const now      = new Date();
  const open     = polls.filter(p => new Date(p.deadline) >= now);
  const expired  = polls.filter(p => new Date(p.deadline) <  now);
  const shown    = tab === 'offen' ? open : expired;

  return (
    <div style={{ minHeight: '100vh', background: '#0f1117' }}>
      <CustomNavbar />
      <div className="container mt-4 pb-5" style={{ maxWidth: 680 }}>

        <h2 style={{ color: '#e8eaf0', fontWeight: 700, marginBottom: 2 }}>Abstimmungen</h2>
        <p style={{ ...S.muted, marginBottom: 20 }}>Laufende und abgeschlossene Umfragen von {cfg.adminRoleName}</p>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* ── Tabs ── */}
        <div className="d-flex gap-2 mb-4">
          {[
            { key: 'offen',     label: `Offen (${open.length})` },
            { key: 'abgelaufen', label: `Abgelaufen (${expired.length})` },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                background:   tab === key ? (key === 'offen' ? '#ffc107' : '#3a3f55') : 'none',
                color:        tab === key ? (key === 'offen' ? '#000' : '#e8eaf0') : '#7b82a0',
                border:       `1px solid ${tab === key ? 'transparent' : '#2e3347'}`,
                borderRadius: 20, padding: '5px 16px', cursor: 'pointer',
                fontWeight:   tab === key ? 600 : 400, fontSize: '0.88rem',
                transition:   'all 0.15s ease',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: '#ffc107' }} />
          </div>
        ) : shown.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#4a5068', padding: '60px 0' }}>
            {tab === 'offen' ? 'Aktuell keine offenen Abstimmungen.' : 'Keine abgeschlossenen Abstimmungen.'}
          </div>
        ) : (
          shown.map(poll => <PollCard key={poll.id} poll={poll} onVoted={load} />)
        )}
      </div>
    </div>
  );
};

export default Polls;
